import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import './GetNoticedEmptyState.css';
import { showError } from '../../../../shared/utils/swalHandler';
import {
  hasBlockingRestrictedContent,
} from '../../../../shared/utils/restrictedWordsUtils';
import { formatAmountWithTwoDecimalPlaces, formatCurrency } from '../../../../shared/utils/function';
import { buildTipPayload, getVatAndServiceFeeAppliedText } from '../../../../shared/utils/paymentUtil';
import {
  MIN_VALUE_TIPS,
  MAX_VALUE_TIPS,
  ADM_TIP_PERCENTAGE,
  DEFAULT_PRESET_AMOUNTS
} from '../../../../shared/constants/common';
import ChatComposerRestrictedWarning from './ChatComposerRestrictedWarning';

/**
 * "Get Noticed" empty state for subscriber when chat has no messages yet.
 * Shows message input, tip amount (same total as tip modal), and "Send without tip" / "Send with tip" actions.
 */
const GetNoticedEmptyState = ({
  creatorInfo,
  sendTextMessage,
  openTipModalRef,
  restrictedWords = [],
  isOnline,
  isSending,
  canSendMessages = true,
  user = null,
}) => {
  const [messageText, setMessageText] = useState('');
  const [tipValue, setTipValue] = useState(MIN_VALUE_TIPS);
  const [totalTipValue, setTotalTipValue] = useState(MIN_VALUE_TIPS);
  const [vatDisplay, setVatDisplay] = useState(0);

  // Same total as tip modal: content price + extra charge + VAT
  useEffect(() => {
    const parsed = parseFloat(tipValue) || 0;
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setTotalTipValue(0);
      setVatDisplay(0);
      return;
    }
    const contentPrice = (parsed * ADM_TIP_PERCENTAGE).toFixed(2);
    let cancelled = false;
    buildTipPayload(contentPrice, parsed, user)
      .then((payload) => {
        if (!cancelled && payload?.amount != null) {
          setTotalTipValue(payload.amount);
          setVatDisplay(payload.vatAmount ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTotalTipValue(formatCurrency(parsed * ADM_TIP_PERCENTAGE));
          setVatDisplay(0);
        }
      });
    return () => { cancelled = true; };
  }, [tipValue, user]);

  const totalTipDisplay = totalTipValue;
  const feeText = getVatAndServiceFeeAppliedText(vatDisplay ?? 0);
  const hasBlocking = hasBlockingRestrictedContent(messageText, restrictedWords);
  const canSendMessage = isOnline && canSendMessages && messageText.trim().length > 0 && !hasBlocking && !isSending;

  const handleSendWithoutTip = useCallback(async () => {
    if (!isOnline) {
      showError('Offline', 'No internet connection. Please reconnect to send messages.');
      return;
    }
    const trimmed = messageText.trim();
    if (!trimmed) return;
    if (hasBlocking) {
      showError(
        'Prohibited content',
        'Your message contains prohibited words or links. Please change or remove them before sending.'
      );
      return;
    }
    const res = await sendTextMessage(trimmed);
    if (res?.success) {
      setMessageText('');
    } else {
      showError('Error', res?.error || 'Failed to send message');
    }
  }, [messageText, hasBlocking, isOnline, sendTextMessage]);

  const handleSendWithTip = useCallback(async () => {
    if (!canSendMessage) return;
    const trimmed = messageText.trim();
    // When empty chat + message with tip: combine into one send (text + "+$X tipped") via handleTipSuccessInChat.
    if (trimmed && openTipModalRef?.current) {
      openTipModalRef.current.pendingMessageWithTip = trimmed;
    }
    // Mark as first-message tip so the API receives isFirstMessageTip=true
    if (openTipModalRef?.current) {
      openTipModalRef.current.isFirstMessageTip = true;
    }
    const result = await openTipModalRef?.current?.sendTipWithAmount?.(tipValue);
    if (result?.success) {
      // Combined message is sent by ChattingPage's handleTipSuccessInChat when pendingMessageWithTip was set.
      setMessageText('');
    }
  }, [canSendMessage, openTipModalRef, tipValue, messageText]);

  const sliderPct = ((parseFloat(tipValue) || MIN_VALUE_TIPS) - MIN_VALUE_TIPS) / (MAX_VALUE_TIPS - MIN_VALUE_TIPS) * 100;
  const isPresetActive = (amount) => Math.abs((parseFloat(tipValue) || 0) - amount) < 0.02;

  return (
    <div className="cm_get_noticed_empty">
      <div className="cm_get_noticed_hero">
        <div className="cm_get_noticed_icon" aria-hidden>
          <i className="fa-solid fa-message" />

        </div>
        <h2 className="cm_get_noticed_title">Get Noticed</h2>
        <p className="cm_get_noticed_subtitle">
          Send a tip to gain priority status and keep this creator notified of your messages
          <span className="cm_get_noticed_crown" aria-hidden> 👑</span>
        </p>
      </div>

      <div className="cm_get_noticed_form">
        <div>
          <label className="cm_get_noticed_label">Message</label>
          <textarea
            className="cm_get_noticed_message_input"
            value={messageText}
            onFocus={(e) => {
              // Ensure the input is visible when keyboard opens.
              // We add a small delay to wait for the keyboard animation to start/finish.
              setTimeout(() => {
                e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 300);
            }}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type your message here..."
            rows={3}
            disabled={!isOnline || isSending || !canSendMessages}
            aria-label="Message"
          />
        </div>
        {hasBlocking && messageText.trim().length > 0 && (
          <p className="cm_get_noticed_error" role="alert">
            This message contains prohibited words or links and cannot be sent.
          </p>
        )}

        <div className="cm_get_noticed_tip_amount_section">
          <div className="cm_get_noticed_tip_amount_header">
            <span className="cm_get_noticed_tip_amount_label">Tip Amount</span>
            <div className="cm_get_noticed_tip_amount_display">
              ${formatAmountWithTwoDecimalPlaces(totalTipDisplay)}
            </div>
          </div>
          <div
            className="cm_get_noticed_slider_wrap"
            style={{ '--jh-tip-slider-value': `${sliderPct}%` }}
          >
            <input
              type="range"
              className="cm_get_noticed_slider"
              min={MIN_VALUE_TIPS}
              max={MAX_VALUE_TIPS}
              step="0.01"
              value={tipValue}
              onChange={(e) => setTipValue(parseFloat(e.target.value) || MIN_VALUE_TIPS)}
              aria-label="Tip amount"
            />
          </div>
          <div className="cm_get_noticed_slider_labels">
            <span className="cm_get_noticed_slider_label">${formatAmountWithTwoDecimalPlaces(formatCurrency(MIN_VALUE_TIPS * ADM_TIP_PERCENTAGE))}</span>
            <span className="cm_get_noticed_slider_label">${formatAmountWithTwoDecimalPlaces(formatCurrency((MAX_VALUE_TIPS * ADM_TIP_PERCENTAGE) / 2))}</span>
            <span className="cm_get_noticed_slider_label">${formatAmountWithTwoDecimalPlaces(formatCurrency(MAX_VALUE_TIPS * ADM_TIP_PERCENTAGE))}</span>
          </div>
          <div className="cm_get_noticed_presets">
            {DEFAULT_PRESET_AMOUNTS.map((amount) => (
              <button
                key={amount}
                type="button"
                className={`cm_get_noticed_preset_btn ${isPresetActive(amount) ? 'cm_get_noticed_preset_active' : ''}`}
                onClick={() => setTipValue(amount)}
              >
                ${(amount * ADM_TIP_PERCENTAGE).toFixed(0)}
              </button>
            ))}
          </div>
          <p className="cm_get_noticed_refund">
            Pssst... if they don&apos;t reply within 10 days you get refunded
          </p>
        </div>

        {!canSendMessages ? (
          <ChatComposerRestrictedWarning
            creatorInfo={creatorInfo}
          />
        ) : (
          <>
            <div className="cm_get_noticed_actions">
              <button
                type="button"
                className="cm_get_noticed_btn cm_get_noticed_btn_outline"
                onClick={handleSendWithoutTip}
                disabled={!canSendMessage}
              >
                <i className="fa-solid fa-paper-plane" aria-hidden />
                Send without tip
              </button>
              <button
                type="button"
                className="cm_get_noticed_btn cm_get_noticed_btn_primary"
                onClick={handleSendWithTip}
                disabled={!canSendMessage}
              >
                <i className="fa-solid fa-paper-plane" aria-hidden />
                Send with tip
              </button>
            </div>
            {feeText ? (
              <p className="cm_get_noticed_fee_text">
                {feeText}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user?.user,
});

export default connect(mapStateToProps)(React.memo(GetNoticedEmptyState));
